import { Component, Input, OnInit, ViewChild } from '@angular/core';
import {
	HistoricosReservatorio,
	ReservatorioVolume,
	ShortReservatorio,
} from '@home/submodulos/dados-meteorologicos/submodulos/monitoramento/interfaces/reservatorios';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
	baixarEAbrirPDF,
	isNotNuloOuUndefined,
	numberToBrNumberTables,
} from '@utils';
import { OptionRadio } from '@shared/interfaces/public-radio-group';
import * as Highcharts from 'highcharts/highstock';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { ADTColumns } from 'angular-datatables/src/models/settings';
import { format } from 'date-fns';
import ptBrLocale from 'date-fns/locale/pt-BR';
import { PublicTableComponent } from '@componentes/public-table/public-table.component';
import { TiposDatePicker } from '@shared/interfaces/date-picker';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { documentDefinitions } from '@utils/pdf-seira';
import { ReservatorioService } from '@modulos/recursos-hidricos/submodulos/reservatorio/services/reservatorio.service';
import moment from 'moment';
import { HistoricoReservatorioResponse } from '@modulos/recursos-hidricos/submodulos/reservatorio/interfaces/bacia-reservatorio-response';
import { DocumentExporter } from '@utils/document-exporter';
import HighchartsExporting from 'highcharts/modules/exporting'; // Módulo principal
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting'; // Módulo para exportação local
import { FormularioRelatorio } from '@modulos/home/submodulos/dados-meteorologicos/interfaces';
import { DateTimeUtils } from '@utils/datetime-util';
import { FiltroPeriodo } from '@home/submodulos/dados-hidrologicos/types/filtro-periodo.type';
HighchartsExporting(Highcharts);
HighchartsOfflineExporting(Highcharts);

@Component({
	selector: 'seira-modal-reservatorios',
	templateUrl: './modal-reservatorios.component.html',
	styleUrls: ['./modal-reservatorios.component.scss'],
})
export class ModalReservatoriosComponent implements OnInit {
	@Input() close!: () => void;
	@Input() reservatorio!: ShortReservatorio;
	@Input() historioReservatorio?: HistoricoReservatorioResponse;
	@ViewChild('tabelaModal', { static: false })
	tabela!: PublicTableComponent;
	reservatorios: ShortReservatorio[] = [];
	dadosGrafico!: HistoricosReservatorio;
	loading = false;
	loadingReservatorios = false;
	dataFimMaxima: Date | null = null;
	gerarGrafico = false;
	unidadesGrafico = [
		{ label: 'Porcentagem', value: 'porcentagem' },
		{ label: 'Metros Cúbicos', value: 'volumeAtual' },
	];
	tiposGrafico = [
		{ label: 'Colunas', value: 'column' },
		{ label: 'Linha', value: 'line' },
		{ label: 'Área', value: 'area' },
		{ label: 'Passos', value: 'steps' },
	];
	periodos = [
		{ label: 'Dia', value: 'day' },
		{ label: 'Mês', value: 'month' },
		{ label: 'Ano', value: 'year' },
	];

	filtroInicial!: FiltroPeriodo;

	private mapeamentoLimitePeriodos: Record<
		string,
		{ amount: number; type: 'day' | 'year' }
	> = {
		day: { amount: 31, type: 'day' },
		month: { amount: 3, type: 'year' },
		year: { amount: 15, type: 'year' },
	};
	dataInicio: Date | null = null;

	form!: FormGroup;
	opcoesVisualizacao: OptionRadio<'grafico' | 'tabelaModal'>[] = [
		{ label: 'Gráfico', value: 'grafico' },
		{ label: 'Tabela', value: 'tabelaModal' },
	];

	formGrafico = this.fb.group({
		unidade: new FormControl('porcentagem'),
		tipoGrafico: new FormControl('column'),
	});
	formTabela = this.fb.group({
		busca: new FormControl(''),
	});
	chartVolume?: Highcharts.Chart;
	buttonsTabela: GroupButton[] = [
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: async () => {
				await this.exportarPDF();
			},
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: async () => {
				await this.exportarCSV();
			},
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: async () => {
				await this.exportarTXT();
			},
		},
	];

	colunasTabela: ADTColumns[] = [
		{
			data: 'dataColeta',
			title: 'Data de coleta',
			className: 'text-center',
			render: (value: ReservatorioVolume['dataColeta'], type: string) => {
				const data = new Date(value);
				const dataFormatada = format(data, 'dd/MM/yyyy', {
					locale: ptBrLocale,
				});
				return type === 'display' ? dataFormatada : value;
			},
		},
		{
			title: 'Volume (%)',
			data: 'porcentagem',
			className: 'text-center',
			render: function (porcentagem: ReservatorioVolume['porcentagem']) {
				return numberToBrNumberTables(porcentagem, 2);
			},
		},
		{
			title: 'Volume (m³)',
			data: 'volumeAtual',
			className: 'text-center',
			render: function (volumeAtual: ReservatorioVolume['volumeAtual']) {
				return numberToBrNumberTables(volumeAtual, 2);
			},
		},
		{
			title: 'Afluência/defluência',
			data: 'afluencia',
			className: 'text-center',
			render: function (afluencia: ReservatorioVolume['afluencia']) {
				return afluencia == 0 || afluencia == 0.0 || afluencia == null
					? '-'
					: numberToBrNumberTables(afluencia, 2);
			},
		},
	];

	constructor(
		private fb: FormBuilder,
		private reservatorioService: ReservatorioService
	) {}

	ngOnInit() {
		this.setForm();
		this.aplicarFiltroInicial();
		this.buscarHistorico();
		this.buscaReservatorios();
		(pdfMake as any).vfs = pdfFonts.pdfMake.vfs;
		this.observeForm();
		this.observeTabela();
	}

	private aplicarFiltroInicial() {
		if (!this.filtroInicial) return;

		const { periodo, mes, dataInicial, dataFinal } = this.filtroInicial;

		if (periodo === 'Mensal' && mes) {
			const d = new Date(mes);

			const inicio = new Date(d.getFullYear(), d.getMonth(), 1);
			const fim = new Date(d.getFullYear(), d.getMonth() + 1, 0);

			this.form.patchValue({
				periodo: 'month',
				dataInicio: inicio,
				dataFim: fim,
			});

			return;
		}

		if (periodo === 'Período personalizado') {
			this.form.patchValue({
				periodo: 'month',
				dataInicio: dataInicial,
				dataFim: dataFinal,
			});

			return;
		}
		if (periodo === 'Últimos volumes') {
			const hoje = new Date();

			this.form.patchValue({
				periodo: 'month',
				dataInicio: new Date(hoje.getFullYear(), hoje.getMonth(), 1),
				dataFim: hoje,
			});
		}
	}

	observeForm() {
		this.form.get('periodo')?.valueChanges.subscribe({
			next: () => {
				this.atualizaDataFimSeNecessario();
				this.atualizaDataFimMaxima();
				this.buscarHistorico();
			},
		});
		this.form.get('dataInicio')?.valueChanges.subscribe({
			next: (value: Date) => {
				this.dataInicio = value;
				this.atualizaDataFimSeNecessario();
				this.atualizaDataFimMaxima();
				this.buscarHistorico();
			},
		});
		this.form.get('dataFim')?.valueChanges.subscribe({
			next: () => {
				this.buscarHistorico();
			},
		});
	}

	private get mapeamentoPeriodoAtual() {
		return this.mapeamentoLimitePeriodos[this.periodoAtual];
	}

	private get isDiferencaDataInicioEFimValida(): boolean {
		const diferenca = moment(this.dataFim).diff(
			this.dataInicio,
			this.mapeamentoPeriodoAtual.type
		);
		return diferenca >= 0 && diferenca <= this.mapeamentoPeriodoAtual.amount;
	}

	private atualizaDataFimSeNecessario() {
		if (
			isNotNuloOuUndefined(this.dataFim) &&
			!this.isDiferencaDataInicioEFimValida
		) {
			const novaDataFim = moment(this.dataInicio).add(
				this.mapeamentoPeriodoAtual.amount,
				this.mapeamentoPeriodoAtual.type
			);
			this.dataFim = (
				novaDataFim.isAfter(new Date()) ? moment(new Date()) : novaDataFim
			).toDate();
		}
	}

	private get periodoAtual(): string {
		return this.form.get('periodo')?.value;
	}

	private atualizaDataFimMaxima() {
		const dataInicioFromForm = this.getFormItemValue(
			FormularioRelatorio.DATA_INICIO
		);
		switch (this.periodoAtual) {
			case 'day':
				const novaDataFimMaximaD = moment(new Date(dataInicioFromForm)).add(
					31,
					'days'
				);
				this.dataFimMaxima =
					DateTimeUtils.getDataOuAtualSeMaior(novaDataFimMaximaD);
				break;
			case 'month':
				const novaDataFimMaximaM = moment(new Date(dataInicioFromForm)).add(
					3,
					'years'
				);
				this.dataFimMaxima =
					DateTimeUtils.getDataOuAtualSeMaior(novaDataFimMaximaM);
				break;
			case 'year':
				const novaDataFimMaximaY = moment(new Date(dataInicioFromForm)).add(
					15,
					'years'
				);
				this.dataFimMaxima =
					DateTimeUtils.getDataOuAtualSeMaior(novaDataFimMaximaY);
				break;
		}
	}

	buscaReservatorios() {
		this.loadingReservatorios = true;
		this.reservatorioService.listShortReservatorios('ALTO', true).subscribe({
			next: reservatorios => {
				this.reservatorios = reservatorios;
				this.loadingReservatorios = false;
			},
			error: () => {
				this.loadingReservatorios = true;
			},
		});
	}
	get modoVisualizacao() {
		return this.form.get('modoVisualizacao')?.value;
	}

	setForm() {
		const dataInicio = new Date();
		const dataFim = new Date();
		dataInicio.setDate(dataInicio.getDate() - 7);
		this.form = this.fb.group({
			reservatorio: new FormControl(
				this.historioReservatorio?.id ?? this.reservatorio.id
			),
			periodo: new FormControl<TiposDatePicker>('day'),
			dataInicio: new FormControl(moment(dataInicio)),
			dataFim: new FormControl(moment(dataFim)),
			modoVisualizacao: new FormControl('grafico'),
		});
		this.dataInicio = dataInicio;
	}

	observeTabela() {
		this.formTabela.get('busca')?.valueChanges.subscribe({
			next: async search => {
				await this.tabela.pesquisar(search || '');
			},
		});
	}

	buscarHistorico() {
		const dataInicio = this.form.get('dataInicio')?.value;
		const dataFim = this.form.get('dataFim')?.value;
		const reservatorio = this.form.get('reservatorio')?.value;
		const periodo: TiposDatePicker = this.form.get('periodo')?.value;

		let id = this.historioReservatorio?.id ?? this.reservatorio.id;
		if (reservatorio) {
			id = parseInt(reservatorio);
		}
		if (dataInicio && dataFim) {
			this.loading = true;
			this.reservatorioService
				.listHistoricoPublicoPorReservatorio(id, dataInicio, dataFim, periodo)
				.subscribe({
					next: historico => {
						this.reservatorio = historico.reservatorio;
						this.dadosGrafico = historico;
						this.loading = false;
					},
					error: () => {
						this.loading = false;
					},
				});
		}
	}

	get tipoPeriodo(): TiposDatePicker {
		return this.form.get('periodo')!.value || 'day';
	}

	private set dataFim(dataFim: Date) {
		this.form.get('dataFim')?.setValue(dataFim, { emitEvent: false });
	}

	get dataFim() {
		return this.form.get('dataFim')!.value || new Date();
	}

	getFormItemValue(formValue: string) {
		return this.form.get(formValue)!.value;
	}

	getFormGraficoValue(formValue: string) {
		return this.formGrafico.get(formValue)!.value;
	}

	async exportarPDF() {
		if (!this.dadosGrafico) return;
		const pdfConfig: any = await documentDefinitions();
		const tabela: any = [];

		tabela.push(
			this.colunasTabela.map(coluna => ({
				text: coluna.title,
				fillColor: '#DCDCDC',
			}))
		);

		this.dadosGrafico.historico.forEach(dadosTabela => {
			tabela.push([
				new Date(dadosTabela.dataColeta).toLocaleString('pt-BR', {
					dateStyle: 'short',
				}),
				numberToBrNumberTables(dadosTabela.porcentagem, 2),
				numberToBrNumberTables(dadosTabela.volumeAtual, 2),
				dadosTabela.afluencia == 0 ||
				dadosTabela.afluencia == 0.0 ||
				dadosTabela.afluencia == null
					? '-'
					: numberToBrNumberTables(dadosTabela.afluencia, 2),
			]);
		});

		pdfConfig.content.push({
			text: `Açude: ${this.dadosGrafico.reservatorio.nome}`,
			alignment: 'center',
			fontSize: 14,
			margin: [0, 5, 0, 15],
		});

		pdfConfig.content.push({
			columns: [
				{
					alignment: 'center',
					text: `Bacia: ${this.dadosGrafico.reservatorio.bacia.nome} `,
					fontSize: 12,
					margin: [0, 0, 0, 8],
				},
				{
					alignment: 'center',
					text: `Capacidade: ${numberToBrNumberTables(
						this.dadosGrafico.reservatorio.capacidade,
						2
					)} m³`,
					fontSize: 12,
					margin: [0, 0, 0, 8],
				},
				{
					alignment: 'center',
					text: `Município: ${this.dadosGrafico.reservatorio.municipio.nome}`,
					fontSize: 12,
					margin: [0, 0, 0, 8],
				},
			],
		});

		pdfConfig.content.push({
			table: {
				alignment: 'center',
				body: tabela.map((row: any[], rowIndex: number) =>
					row.map((cell: any) => ({
						text: cell,
						alignment: 'center',
						fontSize: 12,
						fillColor: rowIndex === 0 ? '#DCDCDC' : null,
						margin: rowIndex === 0 ? [0, 5, 0, 5] : null,
					}))
				),
				widths: ['25%', '25%', '25%', '25%'],
				layout: {
					noWrap: false,
					fontSize: 5,
				},
			},
		} as any);

		baixarEAbrirPDF(this.getNomeArquivo(), pdfConfig);
	}

	private exportarCSV(): void {
		const header = [
			['Agência Executiva de Gestão das Águas do Estado da Paraíba'],
		];
		const title = [[`${this.getNomeArquivo()}\n\n`]];

		const tableData = this.getDadosTabelaReservatorioEstado(
			this.dadosGrafico.historico
		);
		let csvContent = header.concat(title);
		csvContent = csvContent.concat(tableData);
		const csvString = csvContent.map(row => row.join(';')).join('\n');

		DocumentExporter.gerarCSV(csvString, this.getNomeArquivo());
	}

	getDadosTabelaReservatorioEstado(dadosReservatorio: ReservatorioVolume[]) {
		const tableData: any[][] = [];
		const colunas = [
			'Data de coleta',
			'Volume (%)',
			'Volume (m³)',
			'Afluência/defluência',
		];
		tableData.push(colunas);

		dadosReservatorio.forEach(bacia => {
			const rowData = [
				this.formatData(new Date(bacia.dataColeta)),
				numberToBrNumberTables(bacia.porcentagem, 2),
				numberToBrNumberTables(bacia.volumeAtual, 2),
				bacia.afluencia == 0.0 ||
				bacia.afluencia == 0 ||
				bacia.afluencia == null
					? '-'
					: numberToBrNumberTables(bacia.afluencia, 2),
			];
			tableData.push(rowData);
		});

		return tableData;
	}

	private exportarTXT(): void {
		const colunas = [
			'Data de coleta',
			'Volume (%)',
			'Volume (m³)',
			'Afluência/defluência',
		];
		let txtData = `Agência Executiva de Gestão das Águas do Estado da Paraíba\n${this.getNomeArquivo()}\n\n\n`;

		this.dadosGrafico.historico.forEach(bacia => {
			const rowData = [
				this.formatData(new Date(bacia.dataColeta)),
				numberToBrNumberTables(bacia.porcentagem, 2),
				numberToBrNumberTables(bacia.volumeAtual, 2),
				bacia.afluencia == 0.0 ||
				bacia.afluencia == 0 ||
				bacia.afluencia == null
					? '-'
					: numberToBrNumberTables(bacia.afluencia, 2),
			];

			rowData.forEach((data, index) => {
				txtData += `${colunas[index]}: ${data}\n`;
			});

			txtData += '\n';
		});

		DocumentExporter.gerarTXT(txtData, this.getNomeArquivo());
	}

	formatData(data: Date) {
		return new Date(data).toLocaleDateString('pt-BR', {
			year: 'numeric',
			month: 'numeric',
			day: 'numeric',
		});
	}

	setChartRefVolume(chart: Highcharts.Chart) {
		this.chartVolume = chart;
	}

	getNomeArquivo() {
		const dataFormatada = format(
			new Date(),
			"dd 'de' MMMM 'de' yyyy 'às' HH'h'mm",
			{
				locale: ptBrLocale,
			}
		);
		return `Dados hidrológicos exportado em ${dataFormatada}`;
	}
}
